import React from "react";
import cn from "classnames";
import { ContainerPanel } from "@components/organisms/container-panel/container-panel";
import styles from "./dashboard-panel.module.scss";
import { Tile } from "@src/cloud-connect/blueprint/dashboards/entities/tile";
import { DashboardTextPanelContent } from "./dashboard-text-panel-content/dashboard-text-panel-content";
import { DashboardGraphPanelContent } from "./dashboard-graph-panel-content/dashboard-graph-panel-content";
import { RenderIfUserHasPermission } from "@src/components/molecules/render-if-user-has-permission/render-if-user-has-permission";

import { useDashboardPanelHook } from "./use-dashboard-panel-hook";
import { getGridClass } from "./get-grid-class";
import { SpinnerWrapper } from "./spinner-wrapper";
import { ToggleButton } from "@src/components/atoms/toggle-button/toggle-button";
import { ToolTip } from "@src/components/atoms/tooltip/tooltip";
import { useI18N } from "@src/language/language-context";
import { useInViewport } from "@src/hooks/use-in-viewport";
import { DashboardTimeseriesInfoWithPopover } from "../dashboard-timeseries-info-with-popover/dashboard-timeseries-info-with-popover";

interface DashboardPanelProps {
  tile: Tile;
  groupId: string;
}

const cssClassName = "dashboard-panel";

export const DashboardPanel = ({ tile, groupId }: DashboardPanelProps) => {
  const i18n = useI18N();
  const texts = {
    toggleToolTipText: i18n("modules.dashboardPanel.toggleToolTipText"),
    timeSeriesInfoToolTipText: i18n(
      "modules.dashboardPanel.timeSeriesInfoToolTipText",
    ),
  };

  const { ref, inView } = useInViewport<HTMLDivElement>();

  const toggleToolTipPosition = "top";

  const {
    lookForLastTimeStampBeforeFetchingData,
    graphOptionsObject,
    valuesForTextTile,
    onToggle,
    nodeInfoList,
  } = useDashboardPanelHook(tile, groupId, inView);

  return (
    <div
      ref={ref}
      className={cn(styles[cssClassName], getGridClass(tile.getGridArea()))}
    >
      <ContainerPanel>
        {tile.isItATextTile() ? (
          <>
            {valuesForTextTile !== undefined ? (
              <>
                <RenderIfUserHasPermission permission="view-timeseries-info-in-dashboards">
                  <div className={styles[`${cssClassName}__info-icon`]}>
                    <ToolTip
                      content={texts.timeSeriesInfoToolTipText}
                      position={toggleToolTipPosition}
                    >
                      <DashboardTimeseriesInfoWithPopover
                        nodeInfoList={nodeInfoList}
                      />
                    </ToolTip>
                  </div>
                </RenderIfUserHasPermission>
                <div className={styles[`${cssClassName}__toggle-button`]}>
                  <ToolTip
                    content={texts.toggleToolTipText}
                    position={toggleToolTipPosition}
                  >
                    <ToggleButton
                      isActive={lookForLastTimeStampBeforeFetchingData}
                      handleClick={onToggle}
                    />
                  </ToolTip>
                </div>
                <DashboardTextPanelContent
                  title={tile.getTitle()}
                  nodeName={valuesForTextTile.nodeName}
                  value={valuesForTextTile.value}
                  unit={valuesForTextTile.unit}
                />
              </>
            ) : (
              <SpinnerWrapper />
            )}
          </>
        ) : (
          <>
            {graphOptionsObject !== undefined ? (
              <>
                <RenderIfUserHasPermission permission="view-timeseries-info-in-dashboards">
                  <div className={styles[`${cssClassName}__info-icon`]}>
                    <ToolTip
                      content={texts.timeSeriesInfoToolTipText}
                      position={toggleToolTipPosition}
                    >
                      <DashboardTimeseriesInfoWithPopover
                        nodeInfoList={nodeInfoList}
                      />
                    </ToolTip>
                  </div>
                </RenderIfUserHasPermission>
                <div className={styles[`${cssClassName}__toggle-button`]}>
                  <ToolTip
                    content={texts.toggleToolTipText}
                    position={toggleToolTipPosition}
                  >
                    <ToggleButton
                      isActive={lookForLastTimeStampBeforeFetchingData}
                      handleClick={onToggle}
                    />
                  </ToolTip>
                </div>
                <DashboardGraphPanelContent
                  title={tile.getTitle()}
                  graphOptionsObject={graphOptionsObject}
                />
              </>
            ) : (
              <SpinnerWrapper />
            )}
          </>
        )}
      </ContainerPanel>
    </div>
  );
};
